// frontend/src/components/ArticlesCarousel.js
// ─────────────────────────────────────────────────────────────
//  Horizontal auto-scrolling article carousel for the Homepage.
//  Shows 8 cards, auto-scrolls, pause-on-hover, Show More link.
// ─────────────────────────────────────────────────────────────
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { ARTICLES } from '../data/ArticlesData';

const PREVIEW = ARTICLES.slice(0, 8); // show 8 in carousel

const POSTED_FMT = { day: '2-digit', month: 'long', year: 'numeric' };

function CarouselCard({ article }) {
  const posted = new Date(article.date).toLocaleDateString('en-US', POSTED_FMT).toUpperCase();

  return (
    <Link to={`/articles/${article.slug}`} style={{ textDecoration: 'none', display: 'block', width: 280, flexShrink: 0 }}>
      <div
        className="article-card"
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
          height: 360,
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
          cursor: 'pointer',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
          e.currentTarget.style.borderColor = article.accentColor;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = '';
          e.currentTarget.style.boxShadow = '';
          e.currentTarget.style.borderColor = '';
        }}
      >
        {/* Cover */}
        <div style={{
          height: 140, flexShrink: 0, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(135deg, ${article.accentColor}26, ${article.accentColor}0d)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44,
        }}>
          <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 25% 25%, ${article.accentColor}22, transparent 60%)` }} />
          <span style={{ position: 'relative' }}>{article.icon}</span>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 18px 14px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Meta row: posted date + category tag */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.04em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              POSTED {posted}
            </span>
            <span style={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.04em', color: article.accentColor, whiteSpace: 'nowrap' }}>
              #{article.category.replace(/\s+/g, '').toUpperCase()}
            </span>
          </div>

          <h3 style={{
            fontFamily: 'Sora, sans-serif', fontSize: '0.98rem', fontWeight: 700, color: 'var(--text)',
            lineHeight: 1.32, marginBottom: 8, overflow: 'hidden', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {article.title}
          </h3>

          <p style={{
            fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.6, flex: 1,
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
          }}>
            {article.excerpt}
          </p>

          {/* Footer */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, paddingTop: 12,
            borderTop: '1px solid var(--border)', color: article.accentColor,
          }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em' }}>READ ARTICLE</span>
            <ArrowRight size={13} className="article-card-arrow" />
          </div>
        </div>
      </div>
    </Link>
  );
}

// ══════════════════════════════════════════════════════════════
export default function ArticlesCarousel() {
  const trackRef   = useRef(null);
  const [paused, setPaused]   = useState(false);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const CARD_W = 280 + 16; // card width + gap

  const checkBounds = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 0);
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      const el = trackRef.current;
      if (!el) return;
      if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 2) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: CARD_W, behavior: 'smooth' });
      }
      setTimeout(checkBounds, 400);
    }, 3000);
    return () => clearInterval(id);
  }, [paused, checkBounds]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkBounds, { passive: true });
    return () => el.removeEventListener('scroll', checkBounds);
  }, [checkBounds]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * CARD_W * 2, behavior: 'smooth' });
    setTimeout(checkBounds, 400);
  };

  return (
    <section className="section" style={{ background: '#f8fafc', paddingBottom: 64 }}>
      <div className="container">
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <p style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              How-To Guides
              <span style={{ flex: '0 0 36px', height: 1, background: 'linear-gradient(90deg, var(--primary), transparent)', display: 'inline-block' }} />
            </p>
            <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.4rem,3vw,2rem)', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.025em' }}>
              Tool Articles & Tutorials
            </h2>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* Nav buttons */}
            <button
              onClick={() => scroll(-1)}
              disabled={!canPrev}
              style={{ width: 38, height: 38, borderRadius: '50%', background: 'white', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: canPrev ? 'pointer' : 'default', color: canPrev ? 'var(--text)' : '#c5cad3', transition: 'all 0.2s', opacity: canPrev ? 1 : 0.6 }}>
              <ArrowLeft size={16} />
            </button>
            <button
              onClick={() => scroll(1)}
              disabled={!canNext}
              style={{ width: 38, height: 38, borderRadius: '50%', background: 'white', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: canNext ? 'pointer' : 'default', color: canNext ? 'var(--text)' : '#c5cad3', transition: 'all 0.2s', opacity: canNext ? 1 : 0.6 }}>
              <ArrowRight size={16} />
            </button>

            <Link to="/articles" className="btn btn-outline btn-sm" style={{ marginLeft: 4 }}>
              View All →
            </Link>
          </div>
        </div>

        {/* Scrollable track */}
        <div style={{ position: 'relative' }}>
          {/* Left fade */}
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 40, background: 'linear-gradient(90deg, #f8fafc, transparent)', zIndex: 2, pointerEvents: 'none', opacity: canPrev ? 1 : 0, transition: 'opacity 0.3s' }} />
          {/* Right fade */}
          <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(270deg, #f8fafc, transparent)', zIndex: 2, pointerEvents: 'none' }} />

          <div
            ref={trackRef}
            className="articles-track"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            style={{ display: 'flex', gap: 16, overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', paddingBottom: 4, cursor: 'grab' }}
          >
            {PREVIEW.map(article => (
              <CarouselCard key={article.id} article={article} />
            ))}
            {/* Ghost card — "View All" */}
            <Link to="/articles" style={{ textDecoration: 'none', display: 'block', width: 200, flexShrink: 0 }}>
              <div style={{ background: 'white', border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)', height: 360, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = '#fff5f6'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.background = 'white'; }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(0, 92, 252,0.1)', border: '1px solid rgba(0, 92, 252,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ArrowRight size={20} color="var(--primary)" />
                </div>
                <div style={{ textAlign: 'center', padding: '0 20px' }}>
                  <div style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.9rem', fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>View All {ARTICLES.length} Articles</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>PDF guides, image tutorials, and more</div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Mobile: Show More link */}
        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <Link to="/articles" className="btn btn-outline" style={{ padding: '10px 28px' }}>
            Show More Articles →
          </Link>
        </div>
      </div>

      {/* Hide scrollbar + arrow hover nudge */}
      <style>{`
        .articles-track::-webkit-scrollbar { display: none; }
        .article-card:hover .article-card-arrow { transform: translateX(3px); }
        .article-card-arrow { transition: transform 0.2s ease; }
      `}</style>
    </section>
  );
}
